<template>
  <div class="platform-success-page">
    <Loader v-if="loaderEnabled" v-bind:logo="loaderLogo"></Loader>

    <b-row class="justify-content-center">
      <b-col md="10" lg="8">
        <KTCodePreview>
          <template v-slot:preview>
            <div class="platform-success-page__content">
              <div class="text-center mb-4">
                <div class="platform-success-page__icon mb-3">
                  <i class="flaticon2-check-mark text-success"></i>
                </div>
                <h3 class="text-success mb-2">Kết nối thành công!</h3>
                <p class="text-muted">
                  Bạn đã kết nối thành công với {{ platformName }}
                </p>
              </div>

              <StoreAssociationModal
                v-model="showStoreModal"
                :shop-name="shopName"
                :config-id="configId"
                :stores="availableStores"
                @saved="handleStoreSaved"
              />

              <b-card class="platform-success-page__info mb-4">
                <h5 class="mb-4">Thông tin kết nối</h5>

                <b-row>
                  <b-col md="6" class="mb-3">
                    <div class="info-item">
                      <label class="text-muted mb-1"
                        >Sàn thương mại điện tử</label
                      >
                      <div class="d-flex align-items-center">
                        <img
                          :src="getPlatformLogo(platform)"
                          :alt="platformName"
                          class="platform-logo mr-2"
                        />
                        <strong>{{ platformName }}</strong>
                      </div>
                    </div>
                  </b-col>

                  <b-col md="6" class="mb-3">
                    <div class="info-item">
                      <label class="text-muted mb-1">Tên gian hàng</label>
                      <div>
                        <strong>{{ shopName }}</strong>
                      </div>
                    </div>
                  </b-col>

                  <b-col md="6" class="mb-3">
                    <div class="info-item">
                      <label class="text-muted mb-1">Mã gian hàng</label>
                      <div>
                        <code>{{ shopId }}</code>
                      </div>
                    </div>
                  </b-col>

                  <b-col md="6" class="mb-3">
                    <div class="info-item">
                      <label class="text-muted mb-1">Cửa hàng</label>
                      <div
                        v-if="associatedStore"
                        class="d-flex align-items-center"
                      >
                        <strong class="mr-2">{{ associatedStore.name }}</strong>
                        <b-button
                          variant="link"
                          size="sm"
                          class="p-0"
                          @click="showStoreModal = true"
                        >
                          <i class="flaticon2-edit"></i> Thay đổi
                        </b-button>
                      </div>
                      <div v-else>
                        <b-button
                          variant="primary"
                          size="sm"
                          @click="showStoreModal = true"
                        >
                          Liên kết cửa hàng
                        </b-button>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </b-card>

              <b-card
                v-if="statistics"
                class="platform-success-page__stats mb-4"
              >
                <h5 class="mb-4">Thống kê gian hàng</h5>
                <b-row>
                  <b-col md="4" sm="6" class="mb-3">
                    <div class="stat-item text-center">
                      <div class="stat-item__icon mb-2">
                        <i class="flaticon2-box text-primary"></i>
                      </div>
                      <div class="stat-item__value">
                        {{ formatNumber(statistics.productCount) }}
                      </div>
                      <div class="stat-item__label text-muted">Sản phẩm</div>
                    </div>
                  </b-col>

                  <b-col md="4" sm="6" class="mb-3">
                    <div class="stat-item text-center">
                      <div class="stat-item__icon mb-2">
                        <i class="flaticon2-shopping-cart text-warning"></i>
                      </div>
                      <div class="stat-item__value">
                        {{ formatNumber(statistics.pendingOrders) }}
                      </div>
                      <div class="stat-item__label text-muted">
                        Đơn hàng chờ xử lý
                      </div>
                    </div>
                  </b-col>

                  <b-col md="4" sm="6" class="mb-3">
                    <div class="stat-item text-center">
                      <div class="stat-item__icon mb-2">
                        <i class="flaticon2-poll-symbol text-success"></i>
                      </div>
                      <div class="stat-item__value">
                        <b-badge variant="success">Hoạt động</b-badge>
                      </div>
                      <div class="stat-item__label text-muted">Trạng thái</div>
                    </div>
                  </b-col>
                </b-row>
              </b-card>

              <b-alert v-else show variant="info" class="mb-4">
                <i class="flaticon2-information"></i>
                Thống kê gian hàng sẽ được hiển thị sau khi hoàn tất liên kết
                với cửa hàng
              </b-alert>

              <div class="platform-success-page__actions text-center">
                <b-button
                  variant="primary"
                  size="lg"
                  @click="viewConnectionList"
                  class="mr-2 mb-2"
                >
                  <i class="flaticon2-list"></i> Xem danh sách kết nối
                </b-button>
                <b-button
                  variant="outline-primary"
                  size="lg"
                  @click="connectAnother"
                  class="mb-2"
                >
                  <i class="flaticon2-plus"></i> Kết nối thêm sàn khác
                </b-button>
              </div>
            </div>
          </template>
        </KTCodePreview>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import Loader from '@/view/content/Loader.vue';
import { getToastInstance } from '@/utils/toastHelper';
import { mapGetters } from 'vuex';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import StoreAssociationModal from './components/StoreAssociationModal.vue';

const platformRepository = RepositoryFactory.get('platformIntegration');
const storeRepository = RepositoryFactory.get('stores');

export default {
  name: 'PlatformSuccessPage',
  components: {
    KTCodePreview,
    Loader,
    StoreAssociationModal,
  },
  data() {
    return {
      configId: null,
      shopId: null,
      shopName: null,
      platform: null,
      statistics: null,
      associatedStore: null,
      availableStores: [],
      showStoreModal: false,
    };
  },
  computed: {
    ...mapGetters(['layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'));
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo');
    },
    platformName() {
      const names = {
        shopee: 'Shopee',
        tiktok: 'TikTok Shop',
      };
      return names[this.platform] || this.platform || 'sàn thương mại điện tử';
    },
  },
  async created() {
    this.loadBreadcrumb();
    this.extractParams();
    await this.loadStores();
    await this.loadConnectionDetails();

    if (!this.associatedStore && this.availableStores.length > 0) {
      this.$nextTick(() => {
        this.showStoreModal = true;
      });
    }
  },
  methods: {
    ...getToastInstance(),

    loadBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kênh bán' },
        { title: 'Kết nối thành công' },
      ]);
    },

    extractParams() {
      const query = this.$route.query;
      this.configId = query.configId;
      this.shopId = query.shopId;
      this.shopName = query.shopName;
      this.platform = query.platform;
    },

    async loadStores() {
      try {
        const response = await storeRepository.getByUser();
        this.availableStores = response.stores || [];

        if (this.availableStores.length === 0) {
          this.$bvToast.toast(
            'Bạn chưa có cửa hàng nào. Vui lòng tạo cửa hàng để liên kết với gian hàng.',
            {
              title: 'Thông báo',
              variant: 'warning',
              solid: true,
              autoHideDelay: 5000,
            },
          );
        }
      } catch (error) {
        console.error('Error loading stores:', error);
        this.makeToastFailure('Không thể tải danh sách cửa hàng');
      }
    },

    async loadConnectionDetails() {
      if (!this.configId) {
        return;
      }

      try {
        const response = await platformRepository.getConfigDetail(
          this.configId,
        );

        if (response.store) {
          this.associatedStore = response.store;
        }

        if (response.statistics) {
          this.statistics = response.statistics;
        }
      } catch (error) {
        console.error('Error loading connection details:', error);
        this.$bvToast.toast(
          'Không thể tải chi tiết kết nối. Một số thông tin có thể không hiển thị đầy đủ.',
          {
            title: 'Cảnh báo',
            variant: 'warning',
            solid: true,
            autoHideDelay: 5000,
          },
        );
      }
    },

    async handleStoreSaved() {
      this.$bvToast.toast(
        'Đã liên kết gian hàng với cửa hàng thành công. Bạn có thể bắt đầu đồng bộ dữ liệu.',
        {
          title: 'Thành công',
          variant: 'success',
          solid: true,
          autoHideDelay: 4000,
        },
      );
      await this.loadConnectionDetails();
    },

    getPlatformLogo(platform) {
      const logos = {
        shopee: '/img/platforms/shopee.svg',
        tiktok: '/img/platforms/tiktok.svg',
      };
      return logos[platform] || '/img/platforms/default.svg';
    },

    formatNumber(value) {
      if (value === null || value === undefined) {
        return '0';
      }
      return value.toLocaleString('vi-VN');
    },

    viewConnectionList() {
      this.$router.push({ name: 'platform-integration-list' });
    },

    connectAnother() {
      this.$router.push({ name: 'platform-integration-connect' });
    },
  },
};
</script>

<style lang="scss" scoped>
.platform-success-page {
  padding: 2rem 0;

  &__content {
    padding: 2rem 0;
  }

  &__icon {
    i {
      font-size: 4rem;
      display: inline-block;
      width: 100px;
      height: 100px;
      line-height: 100px;
      border-radius: 50%;
      background: #c9f7f5;
    }
  }

  &__info,
  &__stats {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: 1px solid #e4e6ef;

    .info-item {
      label {
        font-size: 0.875rem;
        display: block;
      }

      code {
        background: #f3f6f9;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        font-size: 0.875rem;
      }
    }

    .platform-logo {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }
  }

  &__stats {
    .stat-item {
      padding: 1rem;
      border-radius: 8px;
      background: #f9fafb;
      transition: all 0.3s ease;

      &:hover {
        background: #f3f6f9;
        transform: translateY(-2px);
      }

      &__icon {
        i {
          font-size: 2rem;
        }
      }

      &__value {
        font-size: 1.75rem;
        font-weight: 600;
        color: #3f4254;
      }

      &__label {
        font-size: 0.875rem;
        margin-top: 0.25rem;
      }
    }
  }

  &__actions {
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid #e4e6ef;
  }
}

:deep(.alert-info) {
  background-color: #f1faff;
  border-color: #bee5eb;
  color: #0c5460;

  i {
    margin-right: 0.5rem;
  }
}
</style>
